import type { StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';
import { compareCuratedRanks, getCuratedRank, usePrecomputedEmbeddings } from '@shared/utils/search';
import type { ColorwayColorHit } from './useColorwayColorHits';
import { colorwayOrderForSelection, EMPTY_COLORWAY_ORDER, templateIdsByColorOption } from './useColorwayColorHits';
import { useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';
import { styleFacetOptionForConceptName } from './components/Filters/facetOptions';
import { COLOR_FACET, STYLE_FACET } from './components/Filters/Filters.constants';
import { useMemo } from 'react';

export type TagFilteredTemplates = Readonly<{
  templates: ReadonlyArray<StationeryTemplate>;
  /**
   * `themeId` → colorway ids matching the selected colors, best-first. Empty
   * unless a color filter is active; consumed by tiles to order swatches and
   * preselect the matching colorway, so a card filtered as "blue" renders blue.
   */
  colorwayOrder: ReadonlyMap<string, ReadonlyArray<string>>;
  /**
   * Whether the user has selected any color. Callers use this to stand down
   * behavior that would contradict the selection — notably the display-order
   * flag's repeated tiles, which each pre-select a colorway of their own.
   */
  isColorFilterActive: boolean;
  /** Whether the user has selected any style. */
  isStyleFilterActive: boolean;
}>;

export const useTagFilteredTemplates = (templates: ReadonlyArray<StationeryTemplate>, colorwayColorHits: ReadonlyArray<ColorwayColorHit>): TagFilteredTemplates => {
  const { activePrintFilters } = useDesignGalleryFiltersContext();
  const { themes } = usePrecomputedEmbeddings();

  const selectedColorOptions = useMemo(() => new Set([...activePrintFilters].filter(option => option.startsWith(`${COLOR_FACET}:`))), [activePrintFilters]);
  const selectedStyleOptions = useMemo(() => new Set([...activePrintFilters].filter(option => option.startsWith(`${STYLE_FACET}:`))), [activePrintFilters]);

  // A template's `tags` describe a single palette, so a color filter alone would
  // miss the card whose *other* colorways are that color. Union in the templates
  // matched per colorway; the facet counts do the same union (`useFilterFacets`).
  const colorwayMatchedTemplateIds = useMemo(() => {
    if (!selectedColorOptions.size) return new Set<string>();
    const idsByOption = templateIdsByColorOption(colorwayColorHits);
    const ids = new Set<string>();
    for (const option of selectedColorOptions) for (const id of idsByOption.get(option) ?? []) ids.add(id);
    return ids;
  }, [colorwayColorHits, selectedColorOptions]);

  const filteredTemplates = useMemo(() => {
    if (!activePrintFilters.size) return templates;

    // Mapped tags remain the source for non-style facets only. Style matching
    // comes exclusively from precomputed, color-neutral theme embeddings.
    const mappedTagFilterOptions = new Set([...activePrintFilters].filter(option => !option.startsWith(`${STYLE_FACET}:`)));
    const selectedStyleName = selectedStyleOptions.size === 1 ? [...selectedStyleOptions][0].slice(`${STYLE_FACET}:`.length) : undefined;
    // Style metadata is intentionally read only from the base theme row. The
    // embeddings index is moving to color-neutral theme names, and style tags
    // generally do not vary by color. Do not reintroduce colorway traversal
    // here; colorway iteration belongs only to the COLOR facet logic below.
    const styleMatchesSelection = (template: StationeryTemplate) => {
      if (!themes) return false;
      return (
        themes.get(template.themeId)?.conceptNames.some(conceptName => {
          const option = styleFacetOptionForConceptName(conceptName);
          return option ? selectedStyleOptions.has(option) : false;
        }) ?? false
      );
    };

    const filtered = templates.filter(template => {
      if (template.mappedTags.some(tagCompositeCode => mappedTagFilterOptions.has(tagCompositeCode))) return true;
      if (colorwayMatchedTemplateIds.has(template.id)) return true;
      if (styleMatchesSelection(template)) return true;
      return false;
    });

    // A single selected style can have an editorial order shared with text
    // embeddings search. Keep catalog order as the fallback for tied ranks.
    if (!selectedStyleName || !themes) return filtered;

    const rankedTemplates = filtered.map((template, index) => {
      return { template, index, rank: getCuratedRank(selectedStyleName, { themeId: template.themeId }) };
    });

    return rankedTemplates
      .sort((a, b) => {
        const curatedRankDiff = compareCuratedRanks(a.rank, b.rank);
        if (curatedRankDiff !== 0) return curatedRankDiff;
        return a.index - b.index;
      })
      .map(({ template }) => template);
  }, [templates, activePrintFilters, colorwayMatchedTemplateIds, selectedStyleOptions, themes]);

  const colorwayOrder = useMemo(
    () => (selectedColorOptions.size ? colorwayOrderForSelection(colorwayColorHits, selectedColorOptions) : EMPTY_COLORWAY_ORDER),
    [colorwayColorHits, selectedColorOptions],
  );

  return {
    templates: filteredTemplates,
    colorwayOrder,
    isColorFilterActive: selectedColorOptions.size > 0,
    isStyleFilterActive: selectedStyleOptions.size > 0,
  };
};
